import React, { useCallback } from "react";
import { Image, Form } from "semantic-ui-react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { initialValues, validationSchema } from "./UserForm.form";
import { User } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets";
import { useDropzone } from "react-dropzone";
import "./UserForm.scss";

const userController = new User();

export function UserForm(props) {
  const { close, onReload, user } = props;
  // Los nombres de campo (Nombre, Apellidos, Correo, Contrasena) ya viven en
  // common: aqui solo se anade lo que es propio de este formulario.
  const { t } = useTranslation(["misc", "common"]);
  const { accessToken } = useAuth();
  // El `value` de cada rol es la clave que se guarda en base de datos y NO se
  // traduce; lo unico que cambia de idioma es el `text` que se ve.
  const roleOptions = [
    { key: "admin", value: "admin", text: t("users.roles.admin") },
    { key: "user", value: "user", text: t("users.roles.user") },
    { key: "client", value: "client", text: t("users.roles.client") },
    { key: "partner", value: "partner", text: t("users.roles.partner") },
    { key: "teacher", value: "teacher", text: t("users.roles.teacher") },
    { key: "student", value: "student", text: t("users.roles.student") },
  ];

  const formik = useFormik({
    initialValues: initialValues(user),
    validationSchema: validationSchema(user),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        if (!user) {
          await userController.createUser(accessToken, formValue);
        } else {
          if (formik.values.fileAvatar && user.avatar) {
            await userController.deleteAvatar(accessToken, user.avatar);
          }
          await userController.updateUser(accessToken, user._id, formValue);
        }
        onReload();
        close();
      } catch (error) {
        console.error("Error during form submission:", error);
      }
    },
  });

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const validMimeTypes = ["image/jpeg", "image/png"];
        if (validMimeTypes.includes(file.type)) {
          formik.setFieldValue("avatar", URL.createObjectURL(file));
          formik.setFieldValue("fileAvatar", file);
        } else {
          console.error("Invalid file format or type.");
        }
      }
    },
    [formik],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    onDrop,
    multiple: false,
  });

  const getAvatar = () => {
    if (formik.values.fileAvatar) {
      return formik.values.avatar;
    } else if (formik.values.avatar) {
      return `${formik.values.avatar}`;
    }
    return image.noAvatar;
  };

  return (
    <Form className="user-form" onSubmit={formik.handleSubmit}>
      <div className="user-form__avatar" {...getRootProps()}>
        <input {...getInputProps()} />
        <Image avatar size="small" src={getAvatar()} />
      </div>
      <Form.Group widths="equal">
        <Form.Input
          name="firstname"
          placeholder={t("common:fields.name")}
          onChange={formik.handleChange}
          value={formik.values.firstname}
          error={formik.errors.firstname}
        />
        <Form.Input
          name="lastname"
          placeholder={t("common:fields.surname")}
          onChange={formik.handleChange}
          value={formik.values.lastname}
          error={formik.errors.lastname}
        />
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Input
          name="email"
          placeholder={t("common:fields.email")}
          onChange={formik.handleChange}
          value={formik.values.email}
          error={formik.errors.email}
        />
        <Form.Dropdown
          placeholder={t("users.rolePlaceholder")}
          options={roleOptions}
          selection
          onChange={(e, data) => formik.setFieldValue("role", data.value)}
          value={formik.values.role}
          error={formik.errors.role}
        />
      </Form.Group>

      {/* Mostrar idClient solo si el rol seleccionado es 'client' */}
      {formik.values.role === "client" && (
        <Form.Input
          type="idClient"
          name="idClient"
          placeholder={t("users.form.clientIdPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.idClient}
          error={formik.errors.idClient}
        />
      )}

      <Form.Input
        type="password"
        name="password"
        placeholder={t("common:fields.password")}
        onChange={formik.handleChange}
        value={formik.values.password}
        error={formik.errors.password}
      />
      <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
        {user ? t("users.form.submitUpdate") : t("users.form.submitCreate")}
      </Form.Button>
    </Form>
  );
}
